import {
  AsAppliedOrYieldSummaryProps,
  SectionHeadingProps,
  SummaryFieldProps,
} from '../../../types/stepFlow.type';
import FormAndImageStep from '../../step-flow/FormAndImageStep';
import * as Yup from 'yup';
import ImagePreview from '../../create-trial/ImagePreview';
import notYetProvided from '../../../assets/images/not-yet-provided.png';
import { titleCase } from '../../../utils/helpers/capitalizationUtils';
import { useSubmitAsAppliedMutation } from '../../../redux/services/analysis';
import { useNavigate } from 'react-router-dom';
import { Routes } from 'types/global.type';
import React from 'react';
import {
  formatAnalysisDate,
  generateSummaryField,
} from '../../../utils/helpers/analyzeTrial';
import { AsAppliedSubmitStructure } from '../../../types/analyzeTrial.type';
import { cleanRepsToRemove } from '../RepsToRemoveStep';

const AsAppliedSummaryStep = ({
  inProgressExecution,
  currentShapefile,
  setSavedData,
  shapefileType,
  shapefileGeometry,
  imageUrl,
  fieldResultId,
  trialId,
  trialType,
  overrideInstructions,
  isAdditional,
  ...rest
}: AsAppliedOrYieldSummaryProps) => {
  const navigate = useNavigate();

  let summaryLabel: JSX.Element = overrideInstructions ? (
    <p>{overrideInstructions}</p>
  ) : (
    <p>
      Please confirm the details you've given us about this data are correct,
      then click Save.
    </p>
  );

  if (isAdditional) {
    summaryLabel = (
      <>
        {summaryLabel}
        <br />
        <p>
          For additional passes, plot dimensions and buffer lengths are not
          required; we will use the same values you supplied when uploading the
          first pass for all passes.
        </p>
      </>
    );
  }

  // Initial always-present fields
  let summaryFields: Array<SectionHeadingProps | SummaryFieldProps> = [
    {
      element: 'sectionHeading',
      label: summaryLabel,
    },
    generateSummaryField({
      label: 'Experiment Type',
      summaryValue: titleCase(inProgressExecution.experiment_type || ''),
    }),
    generateSummaryField({
      label: `${shapefileType} Shapefile`,
      summaryValue: (
        <a
          href={currentShapefile.file_data?.signed_url}
          target="_blank"
          rel="noopener noreferrer"
        >
          {currentShapefile.filename}
        </a>
      ),
    }),
  ];

  // insert the column fields for points, if they should be present, at the
  // right point in the list
  if (shapefileGeometry === 'points') {
    summaryFields = summaryFields.concat([
      generateSummaryField({
        label: 'Swath Width column',
        summaryValue: inProgressExecution.swath_width_column,
        unitsField: inProgressExecution.swath_width_units,
      }),
      generateSummaryField({
        label: 'Distance column',
        summaryValue: inProgressExecution.distance_column,
        unitsField: inProgressExecution.distance_units,
      }),
      generateSummaryField({
        label: 'Y Offset column',
        summaryValue: inProgressExecution.y_offset_column,
        unitsField: inProgressExecution.y_offset_units,
      }),
      generateSummaryField({
        label: 'Heading column',
        summaryValue: inProgressExecution.heading_column,
        customUnit: 'degrees',
      }),
    ]);
  }

  // Rate column is always present
  summaryFields = summaryFields.concat([
    generateSummaryField({
      label: `${titleCase(shapefileType)} column`,
      summaryValue: inProgressExecution.rate_column,
      customUnit: `(${inProgressExecution.rate_units})`,
    }),
  ]);

  // reps to remove is also always present
  summaryFields = summaryFields.concat([
    generateSummaryField({
      label: 'Reps to Remove',
      summaryValue: cleanRepsToRemove(inProgressExecution.reps_to_remove),
    }),
  ]);

  // if it's a planting trial, or this is the first pass, we asked for planting date
  if (!isAdditional || trialType === 'planting') {
    summaryFields = summaryFields.concat([
      generateSummaryField({
        label: 'Planting Date',
        summaryValue: formatAnalysisDate(
          (inProgressExecution as AsAppliedSubmitStructure).planting_date,
        ),
      }),
    ]);
  }

  // and if it's not a planting trial, we always ask for application date regardless of pass
  if (trialType !== 'planting') {
    summaryFields.push(
      generateSummaryField({
        label: 'Application Date',
        summaryValue: formatAnalysisDate(
          (inProgressExecution as AsAppliedSubmitStructure).application_date,
        ),
      }),
    );
  }

  const dynamicImage = (
    <ImagePreview imageUrl={imageUrl || notYetProvided} error={null} />
  );

  // this needs to accept data, since FormOnlyStep calls it with data
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const submitAsAppliedCallback = (data: any) => {
    navigate(`${Routes.analyzeTrial}?trial-id=${trialId}`);
  };

  return (
    <FormAndImageStep
      {...rest}
      onSubmitMutationRef={useSubmitAsAppliedMutation}
      successfulSubmitCallback={submitAsAppliedCallback}
      imagePreviewElement={dynamicImage}
      formElements={summaryFields}
      initialValues={{
        ...inProgressExecution,
        field_result_id: fieldResultId,
        trial_id: trialId,
      }}
      validationSchema={Yup.object()}
      submitButtonText={{
        default: 'Save',
        submitting: 'Saving...',
      }}
    >
      {rest.children}
    </FormAndImageStep>
  );
};

export default AsAppliedSummaryStep;
